import { FocusEventHandler, MouseEventHandler, ReactNode } from 'react';

import { Icon, IconName } from 'components/Icon';

import {
  ChildrenContainer,
  IconContainer,
  StyledButton,
  TEXT_COLORS,
} from './Button.styled';
import { ButtonSize, ButtonVariant } from './Button.types';

const DEFAULT_VARIANT = 'primary';

type Props = {
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  children?: ReactNode;
  disabled?: boolean;

  role?: string;
  tabIndex?: number;
  title?: string;

  iconName?: IconName;
  iconPosition?: 'left' | 'right';
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  noPadding?: boolean;

  onBlur?: FocusEventHandler<HTMLButtonElement>;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onFocus?: FocusEventHandler<HTMLButtonElement>;

  'aria-label'?: string;
  'data-qa-id'?: string;
  'data-qa-submitting'?: boolean;
};

export function Button({
  id,
  children,
  className,
  disabled,
  iconName,
  iconPosition = 'left',
  role,
  size,
  tabIndex,
  title,
  type,
  variant,
  fullWidth,
  noPadding,
  onBlur,
  onClick,
  onFocus,
  'aria-label': ariaLabel,
  'data-qa-id': dataQaId,
  'data-qa-submitting': dataQaSubmitting,
}: Props) {
  const activeVariant = variant || DEFAULT_VARIANT;

  return (
    <StyledButton
      id={id}
      className={`${className || ''} ${activeVariant} ${size || 'medium'} ${
        fullWidth ? 'full-width' : ''
      } ${noPadding ? 'no-padding' : ''}`}
      data-qa-id={dataQaId}
      data-qa-submitting={dataQaSubmitting}
      aria-label={ariaLabel}
      type={type === undefined ? 'button' : type}
      disabled={disabled}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      role={role}
      tabIndex={tabIndex}
      title={title}
    >
      {iconName && iconPosition === 'left' && (
        <IconContainer>
          <Icon
            name={iconName}
            size={20}
            initialColorName={TEXT_COLORS[activeVariant]}
          />
        </IconContainer>
      )}

      {children && <ChildrenContainer>{children}</ChildrenContainer>}

      {iconName && iconPosition === 'right' && (
        <IconContainer>
          <Icon
            name={iconName}
            size={20}
            initialColorName={TEXT_COLORS[activeVariant]}
          />
        </IconContainer>
      )}
    </StyledButton>
  );
}
